import { CompanyPartnerRelation } from '../../store/thunks/dashboardThunk';
import { PartnerEnrollmentStatus } from '../enums';

export const homeDepotPartnerId = process.env.REACT_APP_HOME_DEPOT_PARTNER_ID as string;
export const hdSupplyPartnerId = process.env.REACT_APP_HD_SUPPLY_PARTNER_ID as string;
export const capitalWrapsPartnerId = process.env.REACT_APP_CAPITAL_WRAPS_PARTNER_ID as string;
export const spartanToolPartnerId = process.env.REACT_APP_SPARTAN_TOOL_PARTNER_ID as string;
export const winsupplyPartnerId = process.env.REACT_APP_WINSUPPLY_PARTNER_ID as string;
export const weSupplyTradesPartnerId = process.env.REACT_APP_WESUPPLYTRADES_PARTNER_ID as string;
export const coastPartnerId = process.env.REACT_APP_COAST_PARTNER_ID as string;
export const sunbeltRentalsPartnerId = process.env.REACT_APP_SUNBELT_RENTALS_PARTNER_ID as string;
export const everConnectPartnerId = process.env.REACT_APP_EVERCONNECT_PARTNER_ID as string;
export const rinnaiPartnerId = process.env.REACT_APP_RINNAI_PARTNER_ID as string;
export const postcardManiaPartnerId = process.env.REACT_APP_POSTCARD_MANIA_PARTNER_ID as string;
export const behrPartnerId = process.env.REACT_APP_BEHR_PARTNER_ID as string;
export const hdxPartnerId = process.env.REACT_APP_HDX_PARTNER_ID as string;
export const novoPartnerId = process.env.REACT_APP_NOVO_PARTNER_ID as string;
export const baytekPartnerId = process.env.REACT_APP_BAYTEK_PARTNER_ID as string;
export const gustoPartnerId = process.env.REACT_APP_GUSTO_PARTNER_ID as string;

export function canMemberContinuePartnerEnrollment(status: PartnerEnrollmentStatus) {
  return status === PartnerEnrollmentStatus.NotStarted ||
    status === PartnerEnrollmentStatus.ActionRequired;
}

// A distributor (Winsupply, HD Supply, Home Depot) the member has already submitted to and
// that is now with the partner (in progress) or done. Manufacturers that ship through it
// (Rinnai, NOVO, Behr, HDX) should link to it directly instead of re-asking for the
// distributor form, which would reset its status (SN-919).
export function hasMemberEnrolledWithDistributor(status: PartnerEnrollmentStatus) {
  return status === PartnerEnrollmentStatus.InProgress ||
    status === PartnerEnrollmentStatus.Completed;
}

// Only a completed enrollment can be unlinked - unlinking withdraws the member from a
// rewards program they are actually earning in. The unlink endpoint rejects anything else
// with a 400, so the link must not be offered for in-progress or action-required
// enrollments. Consultant-reachout partners (Gusto, Capital Wraps, Baytek, EverConnect,
// PostcardMania) only reach `completed` once ops uploads an account number.
export function canMemberUnlinkPartnerEnrollment(status: PartnerEnrollmentStatus) {
  return status === PartnerEnrollmentStatus.Completed;
}

export function getPaidPartnerEnrollmentStatus(
  enrollmentStatus: PartnerEnrollmentStatus,
  hasRequiredForm: boolean,
  isOptedIn: boolean,
) {
  if (enrollmentStatus !== PartnerEnrollmentStatus.NotStarted) {
    return enrollmentStatus;
  }
  if (isOptedIn) {
    return PartnerEnrollmentStatus.Completed;
  }
  return hasRequiredForm ?
    PartnerEnrollmentStatus.ActionRequired :
    PartnerEnrollmentStatus.NotStarted;
}

export function getPartnerEnrollmentStatus(relation: CompanyPartnerRelation | undefined) {
  if (!relation) {
    return PartnerEnrollmentStatus.NotStarted;
  }

  if (relation.enrollmentStatus) {
    return relation.enrollmentStatus;
  }

  // Preserve the previous display rules for relations whose status has not been backfilled.
  // New enrollment transitions remain owned by the backend-provided enrollmentStatus.
  switch (relation.partnerId) {
    case homeDepotPartnerId:
      return relation.partnerMemberEmail ||
        relation.partnerMemberKey ||
        relation.partnerCodeEnteredInMobile ?
        PartnerEnrollmentStatus.Completed :
        PartnerEnrollmentStatus.NotStarted;
    case winsupplyPartnerId:
    case spartanToolPartnerId:
    case capitalWrapsPartnerId:
    case weSupplyTradesPartnerId:
    case hdSupplyPartnerId:
    case coastPartnerId:
    case sunbeltRentalsPartnerId:
    case everConnectPartnerId:
    case rinnaiPartnerId:
    case postcardManiaPartnerId:
    case behrPartnerId:
    case baytekPartnerId:
    case gustoPartnerId:
    case hdxPartnerId:
    case novoPartnerId:
      return relation.enrollmentCompletedAt ?
        PartnerEnrollmentStatus.Completed :
        PartnerEnrollmentStatus.NotStarted;
    default:
      return PartnerEnrollmentStatus.NotStarted;
  }
}

export function getHomeDepotEnrollmentStatus(
  relation: CompanyPartnerRelation | undefined,
): PartnerEnrollmentStatus {
  return relation?.enrollmentStatus ?? PartnerEnrollmentStatus.NotStarted;
}

export function getPartnerEnrollmentIdentity(relation: CompanyPartnerRelation | undefined) {
  if (!relation) {
    return '';
  }

  const { partnerId } = relation;
  if (!partnerId) {
    return '';
  }

  const prefixStr = ' · ';
  switch (partnerId) {
    case homeDepotPartnerId:
      return relation.partnerMemberEmail ? `${prefixStr}${relation.partnerMemberEmail}` : '';
    case hdSupplyPartnerId:
    case winsupplyPartnerId:
    case sunbeltRentalsPartnerId:
      if (relation.partnerMemberKey) {
        return `${prefixStr}${relation.partnerMemberKey}`;
      }
      return '';
    default:
      return '';
  }
}
